import { jupiterFetch } from '@svenskaspelab/nova-core/jupiter';
import { createLogger } from '@svenskaspelab/nova-core/log';

import { MIDDLEWARE_ERROR_MESSAGES } from '../../../error/constants.js';
import { type ErrorResponse, isErrorResponse } from '../../../utils/errorResponse.js';

const logger = createLogger('payment-sport:grasroot');

export type SponsorshipReceiver = {
  shortname: string;
  forcedInvalidation?: boolean;
};

type SponsorshipResponse = {
  receivers?: SponsorshipReceiver[];
};

export type GrasrotenMessage = {
  hasSponsorship: boolean;
  thankYouMessage?: string;
};

/**
 * Fetches the Gräsroten receivers the player currently sponsors.
 *
 * @returns The receivers selected by the player.
 */
export async function getSponsorships(): Promise<SponsorshipReceiver[]> {
  const response = await jupiterFetch('/player/1/sponsorship/receiverselection?active=true', {
    method: 'GET',
    headers: {
      accept: 'application/json',
    },
    credentials: 'include',
    cache: 'no-store',
  });

  const data = (await response.json()) as SponsorshipResponse | ErrorResponse;

  if (!response.ok || isErrorResponse(data)) {
    throw new Error(MIDDLEWARE_ERROR_MESSAGES.GRASSROOT);
  }

  return data.receivers ?? [];
}

/**
 * Formats receiver names as a readable list, where each club is only mentioned once.
 *
 * @param receivers The Gräsroten receivers to format.
 * @returns The formatted names, for example `AIK, IFK Göteborg och Djurgården`.
 */
export function formatReceiverNames(receivers: readonly SponsorshipReceiver[]): string {
  const shortNames = [...new Set(receivers.map((receiver) => receiver.shortname))];

  if (shortNames.length < 2) {
    return shortNames[0] ?? '';
  }

  return `${shortNames.slice(0, -1).join(', ')} och ${shortNames.at(-1)}`;
}

/**
 * Builds the Gräsroten thank you message shown in the payment done dialog.
 * Receivers that have been forcibly invalidated are ignored.
 *
 * @returns The message and whether the player has an active sponsorship.
 */
export async function getGrasrotenMessageForDialog(): Promise<GrasrotenMessage> {
  try {
    const receivers = await getSponsorships();
    const activeReceivers = receivers.filter((receiver) => !receiver.forcedInvalidation);

    if (activeReceivers.length === 0) {
      return { hasSponsorship: false };
    }

    return {
      hasSponsorship: true,
      thankYouMessage: `${formatReceiverNames(activeReceivers)} tackar för ditt stöd via Gräsroten!`,
    };
  } catch (error) {
    logger.warn(MIDDLEWARE_ERROR_MESSAGES.GRASSROOT, { error: String(error) });

    return { hasSponsorship: false };
  }
}
